import { defaultValues } from '../params/types';
import { createCanvas, get2d } from '../text/rasterize';
import { POST_EFFECTS, POST_EFFECT_BY_ID } from './effects';
import type { Ctx2D, PostChainConfig, PostContext } from './types';

export { POST_EFFECTS, POST_EFFECT_BY_ID };

export function defaultPostChain(): PostChainConfig {
  const config: PostChainConfig = {};
  for (const effect of POST_EFFECTS) {
    config[effect.id] = { enabled: false, values: defaultValues(effect.params) };
  }
  return config;
}

/** Fills in effects and values a stored config is missing — presets survive new effects. */
export function normalisePostChain(config: PostChainConfig | undefined): PostChainConfig {
  const out = defaultPostChain();
  if (!config) return out;
  for (const effect of POST_EFFECTS) {
    const stored = config[effect.id];
    if (!stored) continue;
    out[effect.id] = {
      enabled: stored.enabled === true,
      values: { ...out[effect.id].values, ...stored.values },
    };
  }
  return out;
}

export function anyPostEnabled(config: PostChainConfig): boolean {
  return POST_EFFECTS.some((e) => config[e.id]?.enabled);
}

/**
 * Runs the enabled effects over the canvas, in registry order.
 *
 * A single scratch canvas is created lazily and shared by every effect that needs one.
 * At 2000×2000 each buffer is 16 MB, so allocating one per effect would churn far more
 * memory than the effects themselves cost.
 */
export function applyPostChain(
  ctx: Ctx2D,
  width: number,
  height: number,
  config: PostChainConfig,
  seed: number,
): number {
  const t0 = performance.now();
  let scratchCanvas: OffscreenCanvas | HTMLCanvasElement | null = null;
  let scratchCtx: Ctx2D | null = null;

  const scratch = (): { canvas: OffscreenCanvas | HTMLCanvasElement; ctx: Ctx2D } => {
    if (!scratchCanvas || !scratchCtx) {
      scratchCanvas = createCanvas(width, height);
      scratchCtx = get2d(scratchCanvas);
    }
    scratchCtx.setTransform(1, 0, 0, 1, 0, 0);
    scratchCtx.globalAlpha = 1;
    scratchCtx.globalCompositeOperation = 'source-over';
    scratchCtx.filter = 'none';
    scratchCtx.clearRect(0, 0, width, height);
    return { canvas: scratchCanvas, ctx: scratchCtx };
  };

  for (const effect of POST_EFFECTS) {
    const entry = config[effect.id];
    if (!entry?.enabled) continue;

    const postContext: PostContext = {
      ctx,
      width,
      height,
      values: entry.values,
      seed,
      scratch,
    };
    try {
      ctx.save();
      effect.apply(postContext);
    } finally {
      ctx.restore();
      ctx.filter = 'none';
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  return performance.now() - t0;
}
